import { useInfiniteQuery } from '@tanstack/react-query'
import {
  getFragrancesForCards,
  searchFragrances,
  type IFragranceCardListResult,
} from '../api/fragrances'

// Re-export types
export type { IFragranceCard, IFragranceCardListResult } from '../api/fragrances'
export { getFragranceImageUrl } from '../api/fragrances'

// ============================================================================
// Query Keys
// ============================================================================

export const fragranceKeys = {
  all: ['fragrances'] as const,
  lists: () => [...fragranceKeys.all, 'list'] as const,
  list: (query: string) => [...fragranceKeys.lists(), query] as const,
}

// ============================================================================
// Fragrances Queries
// ============================================================================

/**
 * Infinite query for fetching fragrance cards with optional search
 * Search only triggers when query has at least 2 characters
 */
export function useFragrancesInfinite(searchQuery: string, pageSize: number = 10) {
  return useInfiniteQuery<IFragranceCardListResult, Error>({
    queryKey: fragranceKeys.list(searchQuery),
    queryFn: async ({ pageParam }) => {
      const page = pageParam as number
      // Add small delay for subsequent pages to show loading state
      if (page > 1) {
        await new Promise((resolve) => setTimeout(resolve, 500))
      }

      const trimmedQuery = searchQuery.trim()
      // Only search if query has at least 2 characters
      return trimmedQuery.length >= 2
        ? await searchFragrances(trimmedQuery, page, pageSize)
        : await getFragrancesForCards(page, pageSize)
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1
      return nextPage <= lastPage.totalPages ? nextPage : undefined
    },
    initialPageParam: 1,
  })
}
